/**
 * seWindow — the SE-facing window seam (lane va-ui-desktop-home-2026-10-01, issue #2201).
 *
 * Setpoint-eval suites drive the frontend by writing the REAL zustand stores the renderers
 * read (setpoint-evals/ui-desktop-home/support/home-seams.ts). This module exposes
 * `window.__vaSe` carrying those store handles — but ONLY when the app was loaded with a
 * `?vaSe=1` query param. In normal use the param is absent, the seam never installs, and
 * the product behaves exactly as before (zero product behavior delta — collapse law).
 *
 * Why a query-param gate and not import.meta.env.DEV: the SE harness serves a PRODUCTION
 * build through the ci-gate (scripts/ci-desktop-se-gate.sh), so a dev-only seam would not
 * exist where the specs run. The param is opt-in per page load; the stores hold UI state
 * only (no credentials — auth lives in the va_session cookie), and the persist middleware
 * already exposes the same state to localStorage.
 *
 * THE SEAM IS AN EXPLICIT (TINY) FSM, per docs/frontend-fsm-pattern.md R-A1: the lifecycle
 * is `absent → installed` (on INIT_SE_WINDOW, only when the gate query is present), then
 * `installed → installed` (REGISTER_TTS — the TtsProvider attaches the real shared TTS api;
 * a self-transition: an effect, not a phase change). `TRANSITIONS` is the single source of
 * truth and `nextPhase()` REFUSES pairs the table omits — a duplicate install is refused
 * (idempotent re-init), and registering before install is a no-op. Both guards are the
 * matrix, not ad-hoc ifs, so the census (`setpoint-evals/store-census`) and the co-located
 * spec (`seWindow.spec.ts`, driven by ui-desktop-home/SE-06) pin the real shape.
 *
 * SE-pinned: setpoint-evals/ui-desktop-home/SE-06-sewindow-fsm (matrix + scenario drift +
 * vitest), setpoint-evals/store-census (TRANSITIONS + SE coverage).
 */
import { useComposerStore } from './composerStore';
import { useSessionListStore } from './sessionListStore';
import { useSettingsStore } from './settings.store';

export interface SeWindow {
  stores: {
    composer: unknown;
    sessionList: unknown;
    settings: unknown;
  };
  /** Registered by TtsProvider — the real shared TTS api. */
  tts?: unknown;
}

/** The seam's whole lifecycle: installed or not (the tts handle is an effect, not a phase). */
export type SeInstallPhase = 'absent' | 'installed';
export type SeTrigger = 'INIT_SE_WINDOW' | 'REGISTER_TTS';

/**
 * THE transition matrix (R-A1 — data, enforced by nextPhase below; keep the co-located
 * spec's `row N:` scenarios 1:1 with these rows).
 */
export const TRANSITIONS: ReadonlyArray<{ from: SeInstallPhase; to: SeInstallPhase; trigger: SeTrigger }> = [
  { from: 'absent', to: 'installed', trigger: 'INIT_SE_WINDOW' },
  { from: 'installed', to: 'installed', trigger: 'REGISTER_TTS' },
];

/** Pure (from, trigger) → to, straight from the matrix. Returns null when the pair is
 *  unlisted — callers treat null as a refusal, so the table is load-bearing. */
export function nextPhase(from: SeInstallPhase, trigger: SeTrigger): SeInstallPhase | null {
  const row = TRANSITIONS.find((t) => t.from === from && t.trigger === trigger);
  return row ? row.to : null;
}

/** The live phase, derived from the only ground truth that matters (the window handle). */
function currentPhase(): SeInstallPhase {
  return typeof window !== 'undefined' && window.__vaSe ? 'installed' : 'absent';
}

declare global {
  interface Window {
    __vaSe?: SeWindow;
  }
}

/**
 * Install the seam if (and only if) the page was loaded with ?vaSe=1 AND the matrix
 * admits INIT_SE_WINDOW from the current phase. Idempotent by construction: the second
 * call finds `installed`, the matrix has no row for (installed, INIT_SE_WINDOW), and the
 * refusal is a no-op. Without the gate the seam stays absent — normal users never see it.
 */
export function initSeWindow(): void {
  if (typeof window === 'undefined') return;
  if (!new URLSearchParams(window.location.search).has('vaSe')) return;
  if (nextPhase(currentPhase(), 'INIT_SE_WINDOW') !== 'installed') return;
  window.__vaSe = {
    stores: {
      composer: useComposerStore,
      sessionList: useSessionListStore,
      settings: useSettingsStore,
    },
  };
}

/**
 * Attach a named handle (TtsProvider registers the real TTS api here). A no-op unless the
 * seam is installed AND the matrix admits REGISTER_TTS — registering before install is
 * exactly the unlisted (absent, REGISTER_TTS) pair the matrix refuses.
 */
export function registerSeHandle(name: 'tts', handle: unknown): void {
  if (typeof window === 'undefined' || !window.__vaSe) return;
  if (nextPhase(currentPhase(), 'REGISTER_TTS') !== 'installed') return;
  window.__vaSe[name] = handle;
}
